import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, Loader2, AlertCircle, Globe, Check } from 'lucide-react'
import { toast } from 'react-toastify'
import { useCurrentUser } from '@/features/auth/hooks/use-current-user'
import { useUpdateProfile, validateAvatarFile, MAX_AVATAR_SIZE } from '../hooks/use-update-profile'
import { useUpdateLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../hooks/use-update-locale'
import { cn } from '@/shared/lib/utils'

export function ProfileTab() {
  const { t, i18n } = useTranslation()
  const { user, authUser } = useCurrentUser()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [fullName, setFullName] = useState('')
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const updateProfile = useUpdateProfile(user?.id || '')
  const updateLocale = useUpdateLocale(user?.id || '')

  // Initialize form with current user data
  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '')
    }
  }, [user])

  // Track changes
  useEffect(() => {
    const nameChanged = fullName !== (user?.full_name || '')
    const avatarChanged = avatarFile !== null
    setHasChanges(nameChanged || avatarChanged)
  }, [fullName, avatarFile, user])

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file
    const validation = validateAvatarFile(file)
    if (!validation.valid) {
      setAvatarError(validation.error || null)
      return
    }

    setAvatarError(null)
    setAvatarFile(file)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string)
    }
    reader.readAsDataURL(file)

    // Reset input
    e.target.value = ''
  }

  const validateForm = (): boolean => {
    let isValid = true

    if (fullName.trim().length < 2) {
      setNameError(t('settings.profile.nameMinLength'))
      isValid = false
    } else {
      setNameError(null)
    }

    return isValid
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      await updateProfile.mutateAsync({
        data: {
          full_name: fullName.trim(),
          avatar_url: user?.avatar_url,
        },
        avatarFile: avatarFile || undefined,
      })

      // Reset state
      setAvatarFile(null)
      setAvatarPreview(null)
      setHasChanges(false)

      toast.success(t('settings.profile.updateSuccess'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  const getAvatarDisplay = () => {
    if (avatarPreview) return avatarPreview
    if (user?.avatar_url) return user.avatar_url
    return null
  }

  const getInitials = () => {
    if (fullName) {
      return fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    return authUser?.email?.charAt(0).toUpperCase() || '?'
  }

  const avatarDisplay = getAvatarDisplay()

  const currentLocale = (user?.locale || i18n.language || 'en') as SupportedLocale

  const handleLocaleChange = async (locale: SupportedLocale) => {
    if (locale === currentLocale) return

    try {
      await updateLocale.mutateAsync(locale)
      toast.success(t('settings.profile.languageChanged'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  return (
    <div className="flex flex-col gap-6">
    <form onSubmit={handleSubmit} className="rounded-xl border border-border bg-background">
      {/* Header */}
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-sm font-semibold text-foreground">
          {t('settings.profile.personalInfo')}
        </h2>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-5 p-5">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleAvatarClick}
            className="group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-accent text-white"
          >
            {avatarDisplay ? (
              <img
                src={avatarDisplay}
                alt="Avatar"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-xl font-semibold">{getInitials()}</span>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
              <Camera className="h-5 w-5 text-white" />
            </div>
          </button>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-foreground">
              {t('settings.profile.profilePhoto')}
            </span>
            <button
              type="button"
              onClick={handleAvatarClick}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-background-secondary"
            >
              {t('settings.profile.uploadPhoto')}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Avatar error */}
        {avatarError && (
          <div className="flex items-center gap-2 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {avatarError === 'fileTooLarge'
              ? t('settings.profile.avatarTooLarge', { max: MAX_AVATAR_SIZE / (1024 * 1024) })
              : t('settings.profile.avatarInvalidType')}
          </div>
        )}

        {/* Full Name */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('settings.profile.fullName')}
          </label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder={t('settings.profile.fullNamePlaceholder')}
            className={cn(
              'w-full rounded-lg border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-foreground-subtle focus:outline-none focus:ring-1',
              nameError
                ? 'border-destructive focus:border-destructive focus:ring-destructive'
                : 'border-border focus:border-accent focus:ring-accent'
            )}
          />
          {nameError && (
            <p className="flex items-center gap-1 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" />
              {nameError}
            </p>
          )}
        </div>

        {/* Email (read-only) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('settings.profile.email')}
          </label>
          <input
            type="email"
            value={authUser?.email || ''}
            disabled
            className="w-full rounded-lg border border-border bg-background-secondary px-3.5 py-2.5 text-sm text-foreground-muted"
          />
          <p className="text-xs text-foreground-subtle">
            {t('settings.profile.emailHint')}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border px-5 py-4">
        <button
          type="submit"
          disabled={!hasChanges || updateProfile.isPending}
          className="flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updateProfile.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('settings.profile.saveChanges')}
        </button>
      </div>
    </form>

    {/* Language Preferences */}
    <div className="rounded-xl border border-border bg-background">
      {/* Header */}
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-sm font-semibold text-foreground">
          {t('settings.profile.languageSection')}
        </h2>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
            <Globe className="h-5 w-5 text-accent" />
          </div>
          <div className="flex flex-1 flex-col gap-3">
            <div>
              <span className="text-sm font-medium text-foreground">
                {t('settings.profile.languageLabel')}
              </span>
              <p className="mt-0.5 text-sm text-foreground-muted">
                {t('settings.profile.languageDescription')}
              </p>
            </div>

            {/* Language Options */}
            <div className="flex flex-col gap-2">
              {SUPPORTED_LOCALES.map((locale) => (
                <button
                  key={locale.code}
                  type="button"
                  onClick={() => handleLocaleChange(locale.code)}
                  disabled={updateLocale.isPending}
                  className={cn(
                    'flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors',
                    currentLocale === locale.code
                      ? 'border-accent bg-accent/5'
                      : 'border-border hover:border-foreground-subtle hover:bg-background-secondary',
                    updateLocale.isPending && 'cursor-not-allowed opacity-50'
                  )}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {locale.nativeName}
                    </span>
                    <span className="text-xs text-foreground-muted">
                      {locale.name}
                    </span>
                  </div>
                  {currentLocale === locale.code && (
                    <Check className="h-5 w-5 text-accent" />
                  )}
                  {updateLocale.isPending && updateLocale.variables === locale.code && (
                    <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  )
}
